import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle } from "lucide-react";
import { toast } from "sonner";

const PartySearchList = ({ filteredParties, partySearchQuery, onSelect }) => (
  <div className="mt-3 max-h-64 overflow-y-auto border rounded-lg">
    {filteredParties.map((party) => (
      <div
        key={party.name || party.pan || Math.random()}
        className="p-3 border-b hover:bg-blue-50 cursor-pointer flex justify-between items-center"
        onClick={() => onSelect(party)}
      >
        <div>
          <div className="font-medium">{party.name}</div>
          <div className="text-xs text-slate-500">
            PAN: {party.pan || "N/A"} | GSTIN: {party.gstin || "N/A"}
          </div>
        </div>
        <Button size="sm" variant="outline" className="h-7">
          Select
        </Button>
      </div>
    ))}
    {filteredParties.length === 0 && partySearchQuery.length >= 2 && (
      <div className="p-6 text-center text-slate-500">
        No parties found matching "{partySearchQuery}"
      </div>
    )}
    {partySearchQuery.length < 2 && (
      <div className="p-6 text-center text-slate-500">
        Type at least 2 characters to search...
      </div>
    )}
  </div>
);

export const PartyNameModal = ({
  partyNameModal,
  setPartyNameModal,
  newPartyName,
  setNewPartyName,
  newPartyPan,
  setNewPartyPan,
  showPartySearch,
  setShowPartySearch,
  partySearchQuery,
  setPartySearchQuery,
  handlePartyNameUpdate,
  existingParties,
}) => {
  const filteredParties = useMemo(() => {
    if (!existingParties) return [];
    return existingParties
      .filter(
        (p) =>
          partySearchQuery.length < 2 ||
          p.name?.toLowerCase().includes(partySearchQuery.toLowerCase()) ||
          p.pan?.toLowerCase().includes(partySearchQuery.toLowerCase())
      )
      .slice(0, 30);
  }, [existingParties, partySearchQuery]);

  const close = () => {
    setPartyNameModal({ open: false, oldPartyName: "", txnId: null });
    setShowPartySearch(false);
    setPartySearchQuery("");
  };

  const onSelectParty = (party) => {
    setNewPartyName(party.name);
    setNewPartyPan(party.pan || "");
    setShowPartySearch(false);
    toast.success(`Selected: ${party.name}`);
  };

  return (
    <Dialog open={partyNameModal.open} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Update Party Name</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-sm">
              <strong>Current Party Name:</strong>{" "}
              {partyNameModal.oldPartyName || <span className="text-orange-500">(blank)</span>}
            </p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong>{" "}
              {partyNameModal.oldPartyName
                ? `Updating will apply to ALL transactions with "${partyNameModal.oldPartyName}".`
                : "This will update only THIS single transaction (blank party names are updated individually)."}
            </p>
          </div>
          <div className="flex gap-2 mb-2">
            <Button
              variant={!showPartySearch ? "default" : "outline"}
              size="sm"
              onClick={() => setShowPartySearch(false)}
            >
              Enter Manually
            </Button>
            <Button
              variant={showPartySearch ? "default" : "outline"}
              size="sm"
              onClick={() => setShowPartySearch(true)}
            >
              Search Existing Parties
            </Button>
          </div>
          {!showPartySearch ? (
            <>
              <div>
                <Label className="text-sm font-medium">
                  New Party Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={newPartyName}
                  onChange={(e) => setNewPartyName(e.target.value)}
                  placeholder="Enter party name"
                  className="mt-2"
                  data-testid="party-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">PAN (Optional)</Label>
                <Input
                  value={newPartyPan}
                  onChange={(e) => setNewPartyPan(e.target.value.toUpperCase())}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  className="mt-2 font-mono uppercase"
                  data-testid="party-pan-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  If provided, PAN will also be updated for all matching transactions.
                </p>
              </div>
            </>
          ) : (
            <div>
              <Label className="text-sm font-medium">Search Existing Parties</Label>
              <Input
                value={partySearchQuery}
                onChange={(e) => setPartySearchQuery(e.target.value)}
                placeholder="Type party name or PAN to search..."
                className="mt-2"
                autoFocus
              />
              <PartySearchList
                filteredParties={filteredParties}
                partySearchQuery={partySearchQuery}
                onSelect={onSelectParty}
              />
              <p className="text-xs text-slate-500 mt-2">
                Click on a party to select it. Showing up to 30 results.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="party-name-cancel-btn">Cancel</Button>
          <Button onClick={handlePartyNameUpdate} disabled={!newPartyName.trim()} data-testid="party-name-confirm-btn">
            <CheckCircle className="w-4 h-4 mr-2" />
            Update Party Name
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
